import { Control, useWatch, useFormState } from 'react-hook-form';
import {
    IJobQuestionsForm,
    JobQuestionObjectFields,
    QuestionType,
} from '../../../../../common';
import { IOption } from '../../../../../base';
import {
    QUESTION_TYPES_WITH_DEALBREAKER,
    QUESTION_TYPES_WITH_OPTIONS,
    QUESTION_TYPES_WITH_BOOLEAN_OPTIONS,
    QUESTION_TYPES_WITH_TEXT_EDITOR,
    QUESTION_TYPES_WITH_SHORT_TEXT,
    QUESTION_TYPES_WITH_FILE_UPLOAD,
    QUESTION_TYPES_WITH_DATE_PICKER,
    QUESTION_TYPES_WITH_DATE_RANGE,
    QUESTION_TYPES_WITH_NUMERIC,
} from '../constants';

interface UseQuestionFormStateProps {
    control: Control<IJobQuestionsForm>;
    index: number;
}

export const useQuestionFormState = ({
    control,
    index,
}: UseQuestionFormStateProps) => {
    const { errors, submitCount } = useFormState({ control });
    const fieldNamePrefix = `questions.${index}` as const;
    const questionErrors = errors?.questions?.[index];
    // trigger() (Save / Save & Continue) does not bump submitCount — also
    // treat existing field errors as "submitted" so messages render.
    const hasBeenSubmitted = submitCount > 0 || !!questionErrors;

    const questionType = useWatch({
        control,
        name: `${fieldNamePrefix}.${JobQuestionObjectFields.QUESTION_TYPE}` as `questions.${number}.questionType`,
    });

    const isDealbreaker = useWatch({
        control,
        name: `${fieldNamePrefix}.${JobQuestionObjectFields.IS_DEALBREAKER}` as `questions.${number}.isDealbreaker`,
    });

    const questionTypeValue = (questionType as IOption | null)?.value as
        | string
        | undefined;

    const shouldShowDealbreaker = questionTypeValue
        ? QUESTION_TYPES_WITH_DEALBREAKER.includes(
              questionTypeValue as QuestionType
          )
        : false;

    const shouldShowOptions = questionTypeValue
        ? QUESTION_TYPES_WITH_OPTIONS.includes(
              questionTypeValue as QuestionType
          )
        : false;

    const shouldShowBooleanOptions = questionTypeValue
        ? QUESTION_TYPES_WITH_BOOLEAN_OPTIONS.includes(
              questionTypeValue as QuestionType
          )
        : false;

    const shouldShowTextEditor = questionTypeValue
        ? QUESTION_TYPES_WITH_TEXT_EDITOR.includes(
              questionTypeValue as QuestionType
          )
        : false;

    const shouldShowShortText = questionTypeValue
        ? QUESTION_TYPES_WITH_SHORT_TEXT.includes(
              questionTypeValue as QuestionType
          )
        : false;

    const shouldShowFileUpload = questionTypeValue
        ? QUESTION_TYPES_WITH_FILE_UPLOAD.includes(
              questionTypeValue as QuestionType
          )
        : false;

    const shouldShowDatePicker = questionTypeValue
        ? QUESTION_TYPES_WITH_DATE_PICKER.includes(
              questionTypeValue as QuestionType
          )
        : false;

    const shouldShowDateRange = questionTypeValue
        ? QUESTION_TYPES_WITH_DATE_RANGE.includes(
              questionTypeValue as QuestionType
          )
        : false;

    const shouldShowNumeric = questionTypeValue
        ? QUESTION_TYPES_WITH_NUMERIC.includes(
              questionTypeValue as QuestionType
          )
        : false;

    // Show date picker for both SELECT_DATE and DATE_RANGE types
    const shouldShowDatePickerOrRange =
        shouldShowDatePicker || shouldShowDateRange;

    const hasQuestionTitleError = questionErrors?.questionTitle;
    const optionsError = questionErrors?.options;
    const dealbreakerAnswerError = questionErrors?.dealbreakerAnswer;

    return {
        fieldNamePrefix,
        questionType,
        isDealbreaker,
        questionTypeValue,
        shouldShowDealbreaker,
        shouldShowOptions,
        shouldShowBooleanOptions,
        shouldShowTextEditor,
        shouldShowShortText,
        shouldShowFileUpload,
        shouldShowDatePicker: shouldShowDatePickerOrRange,
        isDateRangeType: shouldShowDateRange,
        shouldShowNumeric,
        hasBeenSubmitted,
        hasQuestionTitleError,
        optionsError,
        dealbreakerAnswerError,
    };
};
